import { useCallback } from 'react';
import CypAdhdReviewDocument from './CypAdhdReviewDocument';
import {
  CYP_SSI_IMPAIRMENT_DOMAINS,
  CYP_SSI_SYMPTOMS,
  symptomOutputKey,
} from '../22_cypAdhdSSI/config/cypAdhdSsiConfig';

/**
 * RD_SSI_CypAdhd — CYP ADHD Semi-Structured Interview review page (ASMT-21).
 *
 * Thin wrapper over the shared CypAdhdReviewDocument, like its CI and DD
 * siblings. The structure is STATIC (unlike the DD's, which is derived from
 * the report doc because its secondary-condition keys are numbered at
 * runtime): the SSI always produces the same 18 symptom sentences and 4
 * impairment summaries, so it is built from the SSI contract.
 *
 * Section titles and per-paragraph titles come from
 * config/cypAdhdSsiConfig.js, which a cross-language test keeps in step with
 * the Python contract — so a clinician reviewing "Losing Items" is reviewing
 * the paragraph generated for that symptom and not its neighbour.
 *
 * The 'ssiReportApproved' flag this page's exit writes is half of what
 * unlocks the Diagnostic Decision (the other half being ciReportApproved —
 * see cyp_adhd_stage_progress.py).
 */

// [display label, structured-entry category] pairs for the AI Inputs panel.
// The category keys are the ones llm_processor_cyp_ssi.py writes into each
// paragraph's structured entry. "Informant Report" is the optional free text
// a parent/carer or school representative typed under a high SNAP-IV 26
// rating; the backend only includes it for a symptom the clinician recorded
// as present, and lists it here so the clinician can see exactly what the
// draft drew on beyond their own decisions and notes. The symptom paragraphs
// are the only ones that carry it; the impairment paragraphs render an empty
// group, as the CI page does for a category with nothing in it.
const SSI_INPUT_PANEL_GROUPS = [
  ['Your decisions', 'Clinician Decisions'],
  ['Your notes', 'Clinician Notes'],
  ['Informant report', 'Informant Report'],
];

export function buildSsiDocStructure() {
  const symptoms = {};
  CYP_SSI_SYMPTOMS.forEach((symptom) => {
    symptoms[symptom.number] = {
      title: symptom.name,
      code: symptomOutputKey(symptom.number),
    };
  });

  const impairment = {};
  CYP_SSI_IMPAIRMENT_DOMAINS.forEach((domain, index) => {
    impairment[index + 1] = {
      title: domain.title,
      code: domain.outputKey,
    };
  });

  return {
    Symptoms: symptoms,
    'Functional Impairment': impairment,
  };
}

export default function RD_SSI_CypAdhd() {
  const buildDocStructure = useCallback(buildSsiDocStructure, []);
  return (
    <CypAdhdReviewDocument
      stageKey="semiStructuredInterview"
      flagPrefix="ssi"
      moduleTitle="Semi-Structured Interview"
      editStageCode="SSI"
      buildDocStructure={buildDocStructure}
      inputPanelGroups={SSI_INPUT_PANEL_GROUPS}
    />
  );
}
